/**
 * ppgTotals — summing projected points without silently dropping a player.
 *
 * 🔴 THE FAILURE THIS EXISTS FOR. A Target carries only key/name/pos/team/price,
 * so a PPG read resolves it back through getPlayerByKey and falls back to the
 * Target itself on a miss. projectPoints scores a bare Target as 0. Per-card
 * that is handled — TargetCell renders nothing rather than "0.0 PPG", which is
 * right, because a confident zero is worse than silence.
 *
 * But a SUM cannot stay silent the same way. Adding 0 into STARTERS PPG makes
 * the tile read as a complete lineup total while quietly omitting a player, and
 * nothing on screen says a name is missing from it. The number is wrong in the
 * one direction a user cannot detect: it looks plausible.
 *
 * Two ways in, both real: the custom free-text entry path puts a name in a plan
 * that was never in the pool, and a saved plan can outlive a player leaving it.
 *
 * So every sum here carries an `unprojected` count beside it, and every place
 * that renders a total renders the count too. No value is guessed for them.
 *
 * 🔴 AND EVERY READER GOES THROUGH HERE, so the user's own PPG override applies
 * uniformly: cards, the starters and bench totals, the printed sheet and both
 * optimizer objectives. Precedence is always override, else engine. The
 * override NEVER reaches getPlayerProj — see usePlanState's PPG sheet for why
 * that separation is the whole safety argument.
 *
 * ── TWO CURRENCIES, AND THEY ARE NOT INTERCHANGEABLE ──────────────────────
 *
 * A RATE (points per game) is what you READ. It is season points over THAT
 * PLAYER'S OWN games — see projectGames. It used to be season points over a
 * literal 17, which was wrong for every skill player (the house projections are
 * normalised to ~15 games on purpose, to price in average games missed) and
 * right only for DEF/K.
 *
 * SEASON POINTS is what you MAXIMISE. 🔴 The optimizer's objective is season
 * points and must stay that way. Dividing by 17 was harmless there — a constant
 * scaling cannot reorder anything — but a per-game rate divides players by
 * DIFFERENT numbers, so optimising it would prefer a high-rate player who misses
 * games over a steadier one worth more points across the season. You draft
 * points, not rates.
 *
 * The two are stated as `rate × games` so the relationship is visible in the
 * code, which also settles what an override MEANS: a PPG override is a RATE, so
 * "Gibbs at 25 per game" is 25 × his games as a season total — 375 on a 15-game
 * projection, not 25 and not 425.
 */

import { getPlayerByKey } from "../../utils/playerIndex";

/** Used only when a games getter is missing or returns nonsense. */
export const GAMES_FALLBACK = 17;

/** That player's own games — guarded, because dividing by 0 renders "Infinity"
 *  on a card rather than as an obvious bug. */
function gamesOf(getPlayerGames, player) {
  const g = Number(getPlayerGames ? getPlayerGames(player) : 0);
  return Number.isFinite(g) && g > 0 ? g : GAMES_FALLBACK;
}

/**
 * PPG for one target, and whether we actually have a projection for them.
 *
 * `projected` is `ppg > 0` rather than "did the key resolve", deliberately.
 * The resolve is not the condition that matters: projectPoints works off
 * name+pos, so a target missing from UNIQUE_PLAYERS can still project fine if
 * the projection source knows the name — and conversely a pool player with no
 * projection data contributes a silent zero exactly like a custom one. The
 * thing worth flagging is the zero, because the zero is what corrupts the sum.
 */
export function makePpgInfo(getPlayerProj, getPlayerGames, overrides = {}) {
  const own = overrides || {};
  return (target) => {
    if (!target) return { ppg: 0, projected: false };
    // Your number wins. An explicit 0 is still YOUR number, so it counts as
    // projected — "unprojected" means we have nothing, not that the value is
    // low, and there is nothing missing when you have said so yourself.
    const mine = own[target.key];
    if (mine !== undefined) return { ppg: mine, projected: true };
    if (!getPlayerProj) return { ppg: 0, projected: false };
    // The resolve matters for the DIVISOR as much as for the points: a bare
    // Target carries no posRank, and games comes off the resolved projection.
    const record = getPlayerByKey(target.key) || target;
    const ppg = (Number(getPlayerProj(record)) || 0) / gamesOf(getPlayerGames, record);
    return { ppg, projected: ppg > 0 };
  };
}

/**
 * The same precedence for a POOL PLAYER rather than a target: the RATE you read
 * on a row. Override, else engine over that player's own games.
 *
 * NOT the optimizer's objective — see makePlayerSeasonPoints, which is.
 */
export function makePlayerPpg(getPlayerProj, getPlayerGames, overrides = {}) {
  const own = overrides || {};
  return (player) => {
    if (!player) return 0;
    const mine = own[`${player.name}-${player.pos}`];
    if (mine !== undefined) return mine;
    return (Number(getPlayerProj ? getPlayerProj(player) : 0) || 0) / gamesOf(getPlayerGames, player);
  };
}

/**
 * 🔴 THE OPTIMIZER'S OBJECTIVE, AND IT IS SEASON POINTS. Written as rate × games
 * so that is legible rather than implied: the rate is your override when you set
 * one and the engine's otherwise, and multiplying it back by that player's games
 * gives the season total the solver maximises.
 *
 * Do NOT swap this for makePlayerPpg. A rate divides players by different
 * numbers, so maximising it would prefer a high-rate player who misses games
 * over a steadier one with more total points — the solver would quietly answer
 * a different question than the one the buttons ask.
 *
 * With no override this returns getPlayerProj UNCHANGED rather than
 * (proj / games) × games: identical in value, exact rather than float-noisy, so
 * the solver's tie-breaking is bit-for-bit what it was before the divisor fix.
 */
export function makePlayerSeasonPoints(getPlayerProj, getPlayerGames, overrides = {}) {
  const own = overrides || {};
  return (player) => {
    if (!player) return 0;
    const mine = own[`${player.name}-${player.pos}`];
    if (mine === undefined) return Number(getPlayerProj ? getPlayerProj(player) : 0) || 0;
    return mine * gamesOf(getPlayerGames, player);
  };
}

/**
 * VORP, shifted by whatever you changed the projection by.
 *
 *   myVorp = baseVorp + (myPpg − basePpg) × games
 *
 * — his OWN games, because VORP is season points and a rate only becomes season
 * points when multiplied by the games it is a rate over.
 *
 * 🔴 THIS IS AN APPROXIMATION, AND A GOOD ONE AT THIS SCALE. Strictly, raising
 * a player's projection also raises the replacement level he is measured
 * against — but replacement is computed across the whole pool (the ~35th RB,
 * the ~45th WR), so one override moves it by a fraction of a point at most, and
 * only if the player sits near the replacement line to begin with.
 *
 * The alternative is worse and much more confusing: a VORP build that silently
 * ignored what you just told it would rank CMC as though you had said nothing,
 * while the PPG build moved. Two objectives disagreeing for an invisible reason
 * is not a tradeoff worth taking for a tenth of a point of precision.
 */
export function makePlayerVorp(getPlayerProj, getPlayerGames, getPlayerVorp, overrides = {}) {
  const own = overrides || {};
  return (player) => {
    if (!player) return 0;
    const base = Number(getPlayerVorp ? getPlayerVorp(player) : 0) || 0;
    const mine = own[`${player.name}-${player.pos}`];
    if (mine === undefined) return base;
    const games = gamesOf(getPlayerGames, player);
    const basePpg = (Number(getPlayerProj ? getPlayerProj(player) : 0) || 0) / games;
    return base + (mine - basePpg) * games;
  };
}

/** Σ ppg over targets, plus how many of them we had no projection for. */
export function sumPpg(targets, ppgInfo) {
  return (targets || []).reduce((acc, target) => {
    if (!target) return acc;
    const { ppg, projected } = ppgInfo(target);
    return { ppg: acc.ppg + ppg, unprojected: acc.unprojected + (projected ? 0 : 1) };
  }, { ppg: 0, unprojected: 0 });
}

/**
 * "118.3" — or "118.3 · 1 unprojected" when the total is incomplete.
 *
 * The qualifier is not optional decoration. Without it the total is a claim the
 * data does not support, and the whole point is that the omission is visible.
 */
export function formatPpgTotal(ppg, unprojected = 0) {
  const base = (Number(ppg) || 0).toFixed(1);
  return unprojected > 0 ? `${base} · ${unprojected} unprojected` : base;
}
